import React, { createContext, useContext, useState, ReactNode } from 'react';

interface PlayerState {
    seenEngagementCounts: boolean | null;
    playerReady: boolean;
    duration: number;
    currentPosition: "" | number;
    // Media whose player reported `currentPosition`; undefined when it was set directly (e.g. reset to 0).
    currentPositionMediaId?: string;
    currentVolumeLevel: number;
    showBackgroundElements: boolean;
    currentInteractiveElement: any;
    currentSceneWidgets: any;
    showLiveProduct: boolean;
    analyticsSent: boolean;
    showShoppingCart: boolean;
    showChatWindow: boolean;
    reactionId: string;
    isPlaying: boolean | undefined;
    polls: any[];
    mediaDurations: any[];
    isPosterRemoved: boolean;
    isLiveStarted: boolean;
    isLiveStopped: boolean | null;
    qnas: any[];
    questions: any[];
    messages: any[];
    adsPlaying: boolean;
    isSticky: boolean;
    startPlayerAt: number | null;
}

interface PlayerContextType extends PlayerState {
    setPlayerReady: (_ready: boolean) => void;
    setIsPlaying: (_isPlaying: boolean | undefined) => void;
    setShowShoppingCart: (_show: boolean) => void;
    setReactionId: (_id: string) => void;
    setShowChatWindow: (_show: boolean) => void;
    setSeenEngagementCounts: (_seen: boolean | null) => void;
    setPlayerCurrentPosition: (_position: "" | number, _mediaId?: string) => void;
    setPlayerDuration: (_duration: number) => void;
    setMediaWithDuration: (_durations: any[]) => void;
    setCurrentVolumeLevel: (_level: number) => void;
    setShowBackgroundElements: (_show: boolean) => void;
    setCurrentInteractiveElement: (_element: any) => void;
    setCurrentSceneWidgets: (_widgets: any) => void;
    setAnalyticsSent: (_sent: boolean) => void;
    setPollsData: (_polls: any[]) => void;
    setRemovePoster: (_value?: boolean) => void;
    setIsLiveStarted: (_value?: boolean) => void;
    setIsLiveStopped: (_value?: boolean) => void;
    setQnAData: (_qnas: any[]) => void;
    setQuestionsData: (_questions: any[]) => void;
    setAdsPlaying: (_playing: boolean) => void;
    setIsSticky: (_sticky: boolean) => void;
    setStartPlayerAt: (_position: number | null) => void;
}

const initialState: PlayerState = {
    seenEngagementCounts: null,
    playerReady: false,
    duration: 0,
    currentPosition: '',
    currentVolumeLevel: 0,
    showBackgroundElements: true,
    currentInteractiveElement: null,
    currentSceneWidgets: null,
    showLiveProduct: false,
    analyticsSent: false,
    showShoppingCart: false,
    showChatWindow: false,
    reactionId: '',
    isPlaying: undefined,
    polls: [],
    mediaDurations: [],
    isPosterRemoved: false,
    isLiveStarted: false,
    isLiveStopped: null,
    qnas: [],
    questions: [],
    messages: [],
    adsPlaying: false,
    isSticky: false,
    startPlayerAt: null,
};

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

export const PlayerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, setState] = useState<PlayerState>(initialState);

    const setPlayerReady = (playerReady: boolean) => setState((s) => ({ ...s, playerReady }));
    const setIsPlaying = (isPlaying: boolean | undefined) => setState((s) => ({ ...s, isPlaying }));
    const setShowShoppingCart = (showShoppingCart: boolean) => setState((s) => ({ ...s, showShoppingCart }));
    const setReactionId = (reactionId: string) => setState((s) => ({ ...s, reactionId }));
    const setShowChatWindow = (showChatWindow: boolean) => setState((s) => ({ ...s, showChatWindow }));
    const setPlayerCurrentPosition = (currentPosition: "" | number, currentPositionMediaId?: string) =>
        setState((s) => ({ ...s, currentPosition, currentPositionMediaId }));
    const setPlayerDuration = (duration: number) => setState((s) => ({ ...s, duration }));
    const setMediaWithDuration = (mediaDurations: any[]) => setState((s) => ({ ...s, mediaDurations }));
    const setCurrentVolumeLevel = (currentVolumeLevel: number) => setState((s) => ({ ...s, currentVolumeLevel }));
    const setShowBackgroundElements = (showBackgroundElements: boolean) => setState((s) => ({ ...s, showBackgroundElements }));
    const setCurrentInteractiveElement = (currentInteractiveElement: any) => setState((s) => ({ ...s, currentInteractiveElement }));
    const setCurrentSceneWidgets = (currentSceneWidgets: any) => setState((s) => ({ ...s, currentSceneWidgets }));
    const setAnalyticsSent = (analyticsSent: boolean) => setState((s) => ({ ...s, analyticsSent }));
    const setPollsData = (polls: any[]) => setState((s) => ({ ...s, polls }));
    const setRemovePoster = (value = true) => setState((s) => ({ ...s, isPosterRemoved: value }));
    const setIsLiveStarted = (value = true) => setState((s) => ({ ...s, isLiveStarted: value }));
    const setIsLiveStopped = (value = true) => setState((s) => ({ ...s, isLiveStopped: value }));
    const setQnAData = (qnas: any[]) => setState((s) => ({ ...s, qnas }));
    const setQuestionsData = (questions: any[]) => setState((s) => ({ ...s, questions }));
    const setAdsPlaying = (adsPlaying: boolean) => setState((s) => ({ ...s, adsPlaying }));
    const setIsSticky = (isSticky: boolean) => setState((s) => ({ ...s, isSticky }));
    const setSeenEngagementCounts = (seenEngagementCounts: boolean | null) => setState((s) => ({ ...s, seenEngagementCounts }));
    const setStartPlayerAt = (startPlayerAt: number | null) => setState((s) => ({ ...s, startPlayerAt }));

    const value: PlayerContextType = {
        ...state,
        setPlayerReady,
        setIsPlaying,
        setShowShoppingCart,
        setReactionId,
        setShowChatWindow,
        setPlayerCurrentPosition,
        setPlayerDuration,
        setMediaWithDuration,
        setCurrentVolumeLevel,
        setShowBackgroundElements,
        setCurrentInteractiveElement,
        setCurrentSceneWidgets,
        setAnalyticsSent,
        setPollsData,
        setRemovePoster,
        setIsLiveStarted,
        setIsLiveStopped,
        setQnAData,
        setQuestionsData,
        setAdsPlaying,
        setIsSticky,
        setSeenEngagementCounts,
        setStartPlayerAt,
    };

    return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
};

export const usePlayer = (): PlayerContextType => {
    const context = useContext(PlayerContext);
    if (!context) {
        throw new Error('usePlayer must be used within a PlayerProvider');
    }
    return context;
};
